// AppModal — padrão visual único para as janelas (modais) do MPires Connect.
// Mudança exclusivamente visual: regras de negócio, cálculos e permissões
// continuam no modal que o consome. Usa Radix Dialog para portal, foco e
// clique-fora/Esc, com UI inteiramente controlada por tokens.
//
// Slots: title, subtitle, action (direito do header), body, highlight, footer.
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@/components/icons";

export const APP_MODAL_TOKENS = Object.freeze({
  // Cores da marca MPires — paleta original restaurada em 03/10/2026.
  // LOTE COR-2 — botão PRIMÁRIO do modal passa a ser MARINHO; hover em CIANO.
  headerBg: "#003E62",       // marinho do logo
  headerText: "#FFFFFF",
  subtitle: "#9FC3D6",       // subtítulo dentro da faixa escura
  primary: "#003E62",        // botão primário em marinho (contraste WCAG alto com texto branco)
  primaryHover: "#008AB0",   // hover em ciano
  accent: "rgba(0,138,176,0.40)",         // azul de destaque: anel, barras, Faturado, ícones ativos
  deepNavy: "#003E62",       // uso pontual
  petrol: "#003E62",         // uso pontual
  cyanLight: "rgba(0,138,176,0.40)",      // apenas fundos/detalhes — nunca texto
  // Compat: alguns consumidores ainda leem o antigo `cyan`.
  cyan: "rgba(0,138,176,0.40)",
  text: "#003E62",
  textSecondary: "#62717F",
  divider: "#EEF2F5",
  highlightBg: "#F3F8FB",
  footerBg: "#FAFCFD",
  success: "#1E7A55",
  alertText: "#A4512A",
  alertBg: "#FCEFE7",
  shadow: "0 24px 48px -12px rgba(10,30,50,.3)",
  radiusModal: 16,
  radiusBlock: 10,
  radiusButton: 8,
});

export function AppModal({
  open,
  onOpenChange,
  title,
  subtitle,
  headerAction,
  children,
  footer,
  size = "lg", // sm | md | lg | xl
  testId,
}) {
  const maxW = { sm: 420, md: 560, lg: 760, xl: 960 }[size] || 760;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          data-testid={testId}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed z-50 bg-white outline-none app-modal-shell data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{
            top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            width: "min(94vw, " + maxW + "px)", maxHeight: "92vh",
            borderRadius: APP_MODAL_TOKENS.radiusModal,
            boxShadow: APP_MODAL_TOKENS.shadow,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            color: APP_MODAL_TOKENS.text,
            overflow: "hidden",
            display: "flex", flexDirection: "column",
          }}
        >
          {/* Faixa azul-marinho do cabeçalho */}
          <div
            className="app-modal-header"
            style={{
              background: APP_MODAL_TOKENS.headerBg,
              color: APP_MODAL_TOKENS.headerText,
              padding: "16px 24px",
              display: "flex", alignItems: "center", gap: 16,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {typeof title === "string" ? (
                <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, letterSpacing: 0.1 }}>{title}</div>
              ) : (
                title
              )}
              {subtitle ? (
                <div style={{ color: APP_MODAL_TOKENS.subtitle, fontSize: 12, marginTop: 2 }}>{subtitle}</div>
              ) : null}
            </div>
            {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
            <DialogPrimitive.Close
              aria-label="Fechar"
              data-testid={testId ? testId + "-close" : undefined}
              className="shrink-0 inline-flex items-center justify-center"
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: "rgba(255,255,255,.08)", color: "#FFFFFF",
              }}
            >
              <X style={{ width: 16, height: 16 }} />
            </DialogPrimitive.Close>
          </div>

          {/* Corpo rolável */}
          <div className="app-modal-body" style={{ padding: 24, overflowY: "auto", flex: 1 }}>
            {children}
          </div>

          {/* Rodapé opcional */}
          {footer ? (
            <div
              className="app-modal-footer"
              style={{
                background: APP_MODAL_TOKENS.footerBg,
                borderTop: "1px solid " + APP_MODAL_TOKENS.divider,
                padding: "16px 24px",
                display: "flex", gap: 12, justifyContent: "flex-end", alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// Botões padronizados do modal — 38px no desktop, 48px no celular (CSS global).
export function AppModalPrimaryButton({ children, style, ...props }) {
  return (
    <button
      {...props}
      className={"app-modal-btn app-modal-btn-primary " + (props.className || "")}
      style={{
        background: APP_MODAL_TOKENS.primary, color: "#FFFFFF",
        borderRadius: APP_MODAL_TOKENS.radiusButton,
        padding: "0 20px", fontSize: 14, fontWeight: 600,
        border: "1px solid " + APP_MODAL_TOKENS.primary,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.6 : 1,
        ...(style || {}),
      }}
    >
      {children}
    </button>
  );
}

export function AppModalSecondaryButton({ children, style, ...props }) {
  return (
    <button
      {...props}
      className={"app-modal-btn app-modal-btn-secondary " + (props.className || "")}
      style={{
        background: "#FFFFFF", color: APP_MODAL_TOKENS.text,
        borderRadius: APP_MODAL_TOKENS.radiusButton,
        padding: "0 20px", fontSize: 14, fontWeight: 500,
        border: "1px solid " + APP_MODAL_TOKENS.divider,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.6 : 1,
        ...(style || {}),
      }}
    >
      {children}
    </button>
  );
}
